import { paginateProductInfoByAdGroup, useContentfulClient } from "@expert/contentful";
import { getLogger } from "@expert/logging";
import { type QueryFunctionContext, type UndefinedInitialDataOptions, useQuery } from "@tanstack/react-query";
import type { ContentfulClientApi } from "contentful";
import { getGamificationApiClient } from "@/sales-tools/home-product/api/api";
import { userCache } from "@/shared-utils";

const logger = getLogger({ module: "checklist-summary-stats", supportTeam: "sales" });

const ADHERENCE_THRESHOLD = 0;

interface ChecklistSummaryQueryProps
    extends Partial<
        UndefinedInitialDataOptions<
            ChecklistSummaryStats | undefined,
            Error,
            ChecklistSummaryStats | undefined,
            ChecklistSummaryStatsKey
        >
    > {
    params: ChecklistSummaryStatsParams;
}

interface ChecklistSummaryStatsParams {
    start: string;
    end: string;
    adGroups: string[];
}

interface ChecklistSummaryRow {
    behavior: string;
    isInvalidData: boolean;
    isWarning: boolean;
    percent: number;
}

export interface ChecklistSummaryStatsResponse {
    totalEligibleSessions: number;
    checklistSummary: { behavior: string; count: number }[];
}

export interface ChecklistSummaryStats {
    checklistSummaryRows: Array<ChecklistSummaryRow>;
    sessionCount: number;
    overallAdherencePercentDisplay: string;
}

export function processChecklistSummaryData(
    stats: ChecklistSummaryStatsResponse,
    behaviors: Map<string, string>,
    adherenceThreshold: number,
): ChecklistSummaryStats {
    const sessionCount = stats?.totalEligibleSessions || 0;
    const checklistSummaryRows = (stats?.checklistSummary || []).map(({ behavior, count }) => {
        const percent = Math.round((count / sessionCount) * 100);

        const isInvalidData = sessionCount === 0;
        const isWarning = percent <= adherenceThreshold;

        return {
            behavior: behaviors.get(behavior) as string,
            isInvalidData,
            isWarning,
            percent,
        };
    });
    const overallAdherencePercent =
        checklistSummaryRows.reduce((total, { percent }) => total + percent, 0) / checklistSummaryRows.length;
    const overallAdherencePercentDisplay =
        Number.isNaN(overallAdherencePercent) || sessionCount === 0 ? "N/A" : `${Math.round(overallAdherencePercent)}%`;

    return {
        checklistSummaryRows,
        sessionCount,
        overallAdherencePercentDisplay,
    };
}

export type ChecklistSummaryStatsKey = [string, ChecklistSummaryStatsParams, string | null];

export function useChecklistSummaryQuery({ params, ...rest }: ChecklistSummaryQueryProps) {
    const contentfulClient = useContentfulClient();

    return useQuery({
        queryKey: ["checklist-summary", params, userCache.employeeId],
        queryFn: (context) => getChecklistSummaryStats(contentfulClient, context),
        ...rest,
    });
}

export async function getChecklistSummaryStats(
    contentfulClient: ContentfulClientApi<undefined>,
    { queryKey: [, { adGroups, end, start }] }: QueryFunctionContext<ChecklistSummaryStatsKey>,
): Promise<ChecklistSummaryStats | undefined> {
    try {
        const behaviors = await buildBehaviorToTitleMap(contentfulClient, adGroups);

        const params = new URLSearchParams({
            start,
            end,
        });

        for (const behavior of behaviors.keys()) {
            params.append("behavior", behavior);
        }

        const result = await getGamificationApiClient()
            .get(`v1/metrics/checklist-summary?${params}`)
            .json<ChecklistSummaryStatsResponse>();

        return processChecklistSummaryData(result, behaviors, ADHERENCE_THRESHOLD);
    } catch (error) {
        logger.error({ error }, "Error fetching checklist summary stats");
    }
}

/**
 * Query Contentful for sales checklists to get which behaviors are being tracked by Gaia in the `FullOfferChecklist`
 * component.
 *
 * @param contentfulClient
 * @param adGroups
 * @returns Map of behaviors keys (stored in the db) to the "title" which is the copy to be displayed in the checklist summary a part of
 * the expert dashboard
 */
async function buildBehaviorToTitleMap(
    contentfulClient: ContentfulClientApi<undefined>,
    adGroups: string[],
): Promise<Map<string, string>> {
    const behaviors = new Map<string, string>();
    const excludedBehaviors = ["RAPPORT", "DEVICE_CUE", "UNSET"];

    for await (const { items } of paginateProductInfoByAdGroup(contentfulClient, { adGroups })) {
        items
            .flatMap((item) => item.fields.salesOfferChecklist?.fields.checklistSections)
            .flatMap((section) => section?.fields.checklistItems)
            .filter(isDefined)
            .forEach(({ fields: { key, title } }) => {
                if (!title) {
                    logger.warn(`Missing 'title' attribute in Contentful for the ${key} behavior`);
                }

                if (!excludedBehaviors.includes(key)) {
                    behaviors.set(key, title || key);
                }
            });
    }

    return behaviors;
}

function isDefined<T>(val?: T): val is Required<T> {
    return Boolean(val);
}
